/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import MixPanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useClientCustomLabelsAll } from 'components/Hooks/useClientCustomLabels';
import { getParentPosition, isEmbedded } from 'utils/embedded';
// eslint-disable-next-line import/no-cycle
import VerificationCodeDialog from 'components/VerificationCodeDialog';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectSendAssessmentResult } from 'containers/Assessment/selectors';
import { setVerificationModalState } from 'containers/Auth/actions';

import ErrorIcon from './images/error.svg';
import SuccessIcon from './images/success.svg';
import CloseIcon from './images/close.svg';

const useStyles = makeStyles(theme => ({
  paper: {
    maxWidth: '672px',
    width: 'calc(100vw - 32px)',
    height: 'auto',
    minHeight: '312px',
    margin: '16px',
    padding: '0px !important',
    position: 'relative',
    backgroundColor: theme.palette.bgColor.white,
    boxShadow:
      '0px 20px 40px rgba(34, 10, 51, 0.2), 0px 0px 15px rgba(34, 10, 51, 0.05)',
    borderRadius: '16px !important',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '20px',
    [theme.breakpoints.down('sm')]: {
      margin: '8px',
      width: 'calc(100vw - 16px)',
      borderRadius: '12px !important',
    },
  },
  dialogContent: {
    padding: '0px !important',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 24,
    right: 24,
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    [theme.breakpoints.down('sm')]: {
      top: 12,
      right: 12,
    },
  },
  contents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '64px 88px 48px',
    width: '100%',
    maxWidth: '672px',
    flex: 'none',
    order: 1,
    alignSelf: 'stretch',
    flexGrow: 0,
    [theme.breakpoints.down('md')]: {
      padding: '48px 32px 24px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '32px 24px 20px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '24px 16px 16px',
    },
    // overrides
    '& p': { textAlign: 'center' },
    '& button': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0px 32px',
      height: '56px',
      background: theme.palette.primary.main,
      borderRadius: '32px',
      flex: 'none',
      order: 0,
      flexGrow: 0,
      color: theme.palette.text.tertiary,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
      minWidth: '123px',
      [theme.breakpoints.down('sm')]: {
        height: '48px',
        padding: '0px 24px',
        fontSize: '16px',
        lineHeight: '22px',
        minWidth: '100px',
        borderRadius: '24px',
      },
      '&:hover': {
        backgroundColor: theme.palette.bgColor.default,
        opacity: 1,
      },
      '&:disabled': {
        backgroundColor: theme.palette.bgColor.default,
        color: theme.palette.text.tertiary,
      },
      '&:disabled:hover': {
        backgroundColor: theme.palette.bgColor.default,
        color: theme.palette.text.tertiary,
      },
      '&:disabled:focus': {
        backgroundColor: theme.palette.bgColor.default,
        color: theme.palette.text.tertiary,
      },
      '&:disabled:active': {
        backgroundColor: theme.palette.bgColor.default,
        color: theme.palette.text.tertiary,
      },
    },
    '& h4': {
      fontWeight: 600,
      fontSize: '32px',
      lineHeight: '38px',
      textAlign: 'center',
      [theme.breakpoints.down('md')]: {
        fontSize: '28px',
        lineHeight: '34px',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '24px',
        lineHeight: '30px',
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '20px',
        lineHeight: '26px',
      },
    },
  },
  successContents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 88px',
    width: '100%',
    maxWidth: '672px',
    flex: 'none',
    order: 1,
    alignSelf: 'stretch',
    flexGrow: 0,
    [theme.breakpoints.down('xs')]: {
      padding: '16px',
    },
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0px',
    gap: '16px',
    width: '100%',
    maxWidth: '496px',
    minHeight: '76px',
    flex: 'none',
    order: 0,
    alignSelf: 'stretch',
    flexGrow: 0,
    [theme.breakpoints.down('sm')]: {
      gap: '12px',
      minHeight: 'auto',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '32px',
    lineHeight: '38px',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: '28px',
      lineHeight: '34px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      lineHeight: '30px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px',
      lineHeight: '26px',
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0px',
    gap: '8px',
    width: '100%',
    maxWidth: '432px',
    minHeight: '64px',
    flex: 'none',
    order: 1,
    flexGrow: 0,
    marginBottom: '48px',
    marginTop: '24px',
  },
  emailInput: {
    width: '100%',
    maxWidth: '432px',
    height: '64px',
    flex: 'none',
    order: 0,
    alignSelf: 'stretch',
    flexGrow: 0,
    [theme.breakpoints.down('sm')]: {
      height: '56px',
    },
    '& .MuiFilledInput-root': {
      borderRadius: '16px',
      height: '64px',
      padding: '0px 16px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '16px',
      background: theme.palette.bgColor.white,
      border: `1px solid ${theme.palette.border.default}`,
      [theme.breakpoints.down('sm')]: {
        height: '56px',
        padding: '0px 12px',
        gap: '12px',
        borderRadius: '12px',
      },
      '&:hover': {
        backgroundColor: theme.palette.bgColor.white,
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.bgColor.white,
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-error': {
        borderColor: `${theme.palette.border.error} !important`,
      },
      '&.Mui-error.Mui-focused': {
        borderColor: `${theme.palette.border.error} !important`,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '18px',
      fontWeight: '300',
      color: `${theme.palette.text.muted}`,
      transform: 'translate(16px, 20px) scale(1)',
      [theme.breakpoints.down('sm')]: {
        fontSize: '16px',
        transform: 'translate(12px, 18px) scale(1)',
      },
      '&.Mui-focused': {
        color: theme.palette.primary.main,
        transform: 'translate(16px, 8px) scale(0.75)',
        [theme.breakpoints.down('sm')]: {
          transform: 'translate(12px, 6px) scale(0.75)',
        },
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(16px, 8px) scale(0.75)',
        [theme.breakpoints.down('sm')]: {
          transform: 'translate(12px, 6px) scale(0.75)',
        },
      },
      '&.Mui-error': {
        color: `${theme.palette.text.error}`,
      },
    },
    '& input': {
      fontSize: '18px !important',
      fontWeight: '600 !important',
      lineHeight: '24px !important',
      color: `${theme.palette.text.primary} !important`,
      fontFamily: 'inherit !important',
      paddingTop: '24px !important',
      [theme.breakpoints.down('sm')]: {
        fontSize: '16px !important',
        lineHeight: '22px !important',
        paddingTop: '20px !important',
      },
    },
  },
  errorMessage: {
    color: '#CA1621',
    fontSize: '16px',
    fontWeight: 300,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '432px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    isolation: 'isolate',
    width: '100%',
    maxWidth: '496px',
    flex: 'none',
    order: 4,
    alignSelf: 'stretch',
    flexGrow: 0,
    [theme.breakpoints.down('sm')]: {
      height: '72px',
    },
  },
  submitButton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px 32px',
    height: '56px',
    background: theme.palette.primary.main,
    borderRadius: '32px',
    flex: 'none',
    order: 0,
    flexGrow: 0,
    color: theme.palette.text.tertiary,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    minWidth: '123px',
    [theme.breakpoints.down('sm')]: {
      height: '48px',
      padding: '0px 24px',
      fontSize: '16px',
      lineHeight: '22px',
      minWidth: '100px',
      borderRadius: '24px',
    },
    '&:hover': {
      background: theme.palette.primary.main,
    },
    '&:disabled': {
      background: theme.palette.primary.main,
      color: theme.palette.text.tertiary,
    },
    '&:disabled:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.text.tertiary,
    },
    '&:disabled:focus': {
      background: theme.palette.primary.main,
      color: theme.palette.text.tertiary,
    },
    '&:disabled:active': {
      background: theme.palette.primary.main,
      color: theme.palette.text.tertiary,
    },
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },

  successMessage: {
    fontWeight: 600,
    fontSize: '32px',
    lineHeight: '38px',
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('md')]: {
      fontSize: '28px',
      lineHeight: '34px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      lineHeight: '30px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px',
      lineHeight: '26px',
    },
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '2px',
    marginBottom: '12px',
  },
}));

const CustomEmailResultsModal = ({
  open,
  onClose,
  onEmailSent,
  assessment,
}) => {
  const classes = useStyles();
  const { isVariantB, shouldABTest } = useContext(AssessmentResultContext);
  const { slug } = useParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [assessmentSiteCopy, verifyEmailSiteCopy] = useSiteCopySelector([
    'assessment',
    'verify-email',
  ]);
  const [embedParentPosition, setEmbedParentPosition] = useState({});
  const isEmbed = isEmbedded();
  const clientCustomLabels = useClientCustomLabelsAll(assessmentSiteCopy);
  const verifyEmailCustomLabels = useClientCustomLabelsAll(verifyEmailSiteCopy);
  const [loading, setLoading] = useState(false);
  const sendAssessmentResult = useSelector(makeSelectSendAssessmentResult());
  const dispatch = useDispatch();
  const verifyEmailSubtitle = _get(
    verifyEmailCustomLabels,
    'verificationCodeLabels.subtitle',
  );
  const verifyEmailSubmitButtonLabel = _get(
    verifyEmailCustomLabels,
    'verificationCodeLabels.submit',
  );
  const enterEmailLabel = _get(
    clientCustomLabels,
    'enterEmailLabel',
    'Enter your email to receive your results.',
  );
  const emailAddressLabel = _get(
    clientCustomLabels,
    'emailAddressLabel',
    'Email address',
  );
  const invalidEmailLabel = _get(
    clientCustomLabels,
    'invalidEmailLabel',
    'Please enter a valid email address',
  );
  const missingEmailLabel = _get(
    clientCustomLabels,
    'missingEmailLabel',
    'Please enter an email address',
  );
  const failedToSendEmailLabel = _get(
    clientCustomLabels,
    'failedToSendEmailLabel',
    'Failed to send email. Please try again.',
  );

  const successMessageLabel = _get(
    clientCustomLabels,
    'successEmailLabel',
    'Your results have been emailed.',
  );
  const submitButtonLabel = _get(
    clientCustomLabels,
    'submitButtonLabel',
    'Submit',
  );
  const assessmentID = _get(assessment, 'sys.id', '');
  const name = _get(assessment, 'fields.name', '');

  useEffect(() => {
    let isMounted = true;

    const fetchAndSetParentPosition = async () => {
      const parentPosition = await getParentPosition();
      if (isMounted) {
        setEmbedParentPosition(parentPosition);
      }
    };
    if (open && isEmbed) {
      fetchAndSetParentPosition();
    }

    return () => {
      isMounted = false;
    };
  }, [open, isEmbed]);

  // Listen to email sending result
  useEffect(() => {
    if (sendAssessmentResult.error === false) {
      // Email sent successfully
      setIsSuccess(true);
      setIsSubmitted(false);
      setShowVerificationDialog(false);
      setLoading(false);
    } else if (sendAssessmentResult.error === true) {
      // Email sending failed
      setError(failedToSendEmailLabel);
      setIsSubmitted(false);
      setLoading(false);
    }
  }, [sendAssessmentResult]);

  const validateEmail = emailAddress => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
  };

  const handleEmailChange = event => {
    const { value } = event.target;
    setEmail(value);
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!email.trim()) {
      setError(missingEmailLabel);
      return;
    }

    if (!validateEmail(email)) {
      setError(invalidEmailLabel);
      return;
    }

    setIsSubmitted(true);
    setError('');

    try {
      // Track MixPanel event
      MixPanel.track(
        'Assessment Results - Email Results - Clicked',
        injectAssessmentResultsABData(
          {
            slug,
            assessmentName: name,
            assessmentId: assessmentID,
            page: window.location.pathname,
            path: 'assessment results',
            type: 'modal',
            email: email.trim(),
          },
          shouldABTest,
          isVariantB,
        ),
      );
      dispatch(
        setVerificationModalState({
          payload: { email, isGuest: true },
          info: { type: 'GUEST_VERIFICATION' },
        }),
      );
      setShowVerificationDialog(true);
    } catch (err) {
      setError(failedToSendEmailLabel);
      setIsSubmitted(false);
    }
  };

  const sendAssessmentResults = async () => {
    setLoading(true);

    // Call the handleEmailResults reducer
    if (onEmailSent) {
      await onEmailSent(email.trim());
    }
  };

  const handleClose = (event, reason) => {
    setEmail('');
    setError('');
    setIsSubmitted(false);
    setIsSuccess(false);
    if (showVerificationDialog) {
      MixPanel.track('Email Verification - Canceled', {
        cancel_action: reason || 'unknown',
        trigger: 'guest verification',
        type: 'modal',
      });
      setShowVerificationDialog(false);
    }
    onClose();
  };

  const renderContent = () => {
    if (showVerificationDialog) {
      return (
        <div className={classes.contents}>
          <VerificationCodeDialog
            contentOnly
            type="PAGE"
            onSuccess={sendAssessmentResults}
            showLoadingOverlay={false}
            loading={loading}
            subtitle={verifyEmailSubtitle}
            submitLabel={verifyEmailSubmitButtonLabel}
            isEmbedded
          />
        </div>
      );
    }
    if (isSuccess) {
      return (
        <div className={classes.successContents}>
          <div className={classes.headerSection}>
            <img src={SuccessIcon} alt="Success" />
            <Typography className={classes.successMessage}>
              {successMessageLabel}
            </Typography>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.contents}>
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div className={classes.headerSection}>
            <Typography className={classes.title}>{enterEmailLabel}</Typography>
          </div>

          <div className={classes.inputContainer}>
            <TextField
              className={classes.emailInput}
              variant="filled"
              label={emailAddressLabel}
              value={email}
              onChange={handleEmailChange}
              error={!!error}
              disabled={isSubmitted}
              autoFocus
              InputProps={{
                disableUnderline: true,
                style: {
                  height: '64px',
                  borderRadius: '16px',
                  backgroundColor: 'white',
                  border: error ? `3px solid #CA1621` : `1px solid #B8B9BB`,
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: '18px',
                  fontWeight: '300',
                  color: '#000000',
                },
              }}
              inputProps={{
                'aria-label': emailAddressLabel,
              }}
            />
            {error && (
              <div className={classes.errorContainer}>
                <img src={ErrorIcon} alt="Error" />
                <Typography className={classes.errorMessage}>
                  {error || invalidEmailLabel}
                </Typography>
              </div>
            )}
          </div>

          <div className={classes.buttonContainer}>
            <Button
              type="submit"
              className={classes.submitButton}
              disabled={isSubmitted || loading}
            >
              {isSubmitted ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                submitButtonLabel
              )}
            </Button>
          </div>
        </form>
      </div>
    );
  };

  const embedPositionTop = _get(embedParentPosition, 'top', null);

  const dialogProps = {
    onClose: handleClose,
    'aria-labelledby': 'blueshieldca-custom-email-results-title',
    open,
    classes: {
      paper: classes.paper,
    },
  };

  if (embedPositionTop && isEmbed) {
    dialogProps.PaperProps = {
      style: {
        position: 'fixed',
        top: `${embedPositionTop}px`,
      },
    };
  }

  return (
    <Dialog
      {...dialogProps}
      disableBackdropClick={isSubmitted}
      disableEscapeKeyDown={isSubmitted}
    >
      <DialogContent className={classes.dialogContent}>
        <IconButton
          className={classes.closeButton}
          onClick={e => handleClose(e, 'close_button')}
          aria-label="Close"
        >
          <img src={CloseIcon} alt="Close" />
        </IconButton>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default CustomEmailResultsModal;
